import { coachProfileService } from '@/services/coach-profile.service'
import type {
  CoachAutosaveDraft,
  CoachAvailabilityOverrideWrite,
  CoachBackground,
  CoachCredential,
  CoachExpertiseWrite,
  CoachNotableClient,
  CoachProfilePatch,
  CoachPublicProfileWrite,
  CoachRoleRecord,
  CoachSessionTypeWrite,
  CoachThoughtLeadership,
  CoachTrack,
  CoachVetting,
  CoachWeeklyAvailability,
} from '@/types/coach'
import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

export const coachProfileKeys = {
  all: ['coach-profile'] as const,
  availability: ['coach-availability'] as const,
  availabilityOverrides: (startDate: string, endDate: string) =>
    ['coach-availability-overrides', startDate, endDate] as const,
  sessionTypes: ['coach-session-types'] as const,
  vetting: ['coach-vetting'] as const,
  background: ['coach-background'] as const,
  autosave: ['coach-profile-autosave'] as const,
}

function markApprovedProfileRevisionDraft(queryClient: QueryClient) {
  queryClient.setQueryData<CoachVetting>(coachProfileKeys.vetting, (current) =>
    current?.status === 'approved'
      ? {
          ...current,
          revision_status: 'draft',
          revision_note: null,
          revision_submitted_at: null,
          has_unpublished_changes: true,
        }
      : current,
  )
}

function syncVettingAfterProfileSave(queryClient: QueryClient, profile: { status: string | null }) {
  if (profile.status === 'approved') {
    markApprovedProfileRevisionDraft(queryClient)
    return
  }
  if (profile.status !== 'draft') return
  queryClient.setQueryData<CoachVetting>(coachProfileKeys.vetting, (current) =>
    current?.status === 'pending_review'
      ? {
          ...current,
          status: 'draft',
          note: null,
          reviewed_at: null,
        }
      : current,
  )
}

export function useCoachAutosave() {
  return useQuery({
    queryKey: coachProfileKeys.autosave,
    queryFn: coachProfileService.getAutosave,
  })
}

export function useUpdateCoachAutosave() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: CoachAutosaveDraft) => coachProfileService.updateAutosave(payload),
    onSuccess: (draft) => queryClient.setQueryData(coachProfileKeys.autosave, draft),
  })
}

function cacheProfileBackgroundRevision(queryClient: QueryClient, background: CoachBackground) {
  queryClient.setQueryData(coachProfileKeys.background, background)
  markApprovedProfileRevisionDraft(queryClient)
  queryClient.invalidateQueries({ queryKey: coachProfileKeys.vetting })
}

export function useCoachBackground() {
  return useQuery({
    queryKey: coachProfileKeys.background,
    queryFn: coachProfileService.getBackground,
  })
}

export function useCredentialCatalog(query: string) {
  return useQuery({
    queryKey: ['coach-credential-catalog', query.trim().toLowerCase()],
    queryFn: () => coachProfileService.searchCredentialCatalog(query.trim()),
    enabled: query.trim().length > 0,
    staleTime: 5 * 60 * 1000,
  })
}

export function useUpdateCoachBackground() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: CoachBackground) => coachProfileService.updateBackground(payload),
    onSuccess: (background) => cacheProfileBackgroundRevision(queryClient, background),
  })
}

export function useCreateCoachCredential() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: CoachCredential) => coachProfileService.createCredential(payload),
    onSuccess: (background) => queryClient.setQueryData(coachProfileKeys.background, background),
  })
}

export function useUpdateCoachCredential() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: CoachCredential }) =>
      coachProfileService.updateCredential(id, payload),
    onSuccess: (background) => queryClient.setQueryData(coachProfileKeys.background, background),
  })
}

export function useDeleteCoachCredential() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => coachProfileService.deleteCredential(id),
    onSuccess: (background) => queryClient.setQueryData(coachProfileKeys.background, background),
  })
}

export function useCreateCoachAffiliation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: CoachRoleRecord) => coachProfileService.createAffiliation(payload),
    onSuccess: (background) => cacheProfileBackgroundRevision(queryClient, background),
  })
}

export function useUpdateCoachAffiliation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: CoachRoleRecord }) =>
      coachProfileService.updateAffiliation(id, payload),
    onSuccess: (background) => cacheProfileBackgroundRevision(queryClient, background),
  })
}

export function useDeleteCoachAffiliation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => coachProfileService.deleteAffiliation(id),
    onSuccess: (background) => cacheProfileBackgroundRevision(queryClient, background),
  })
}

export function useCreateCoachTeachingRole() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: CoachRoleRecord) => coachProfileService.createTeachingRole(payload),
    onSuccess: (background) => cacheProfileBackgroundRevision(queryClient, background),
  })
}

export function useUpdateCoachTeachingRole() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: CoachRoleRecord }) =>
      coachProfileService.updateTeachingRole(id, payload),
    onSuccess: (background) => cacheProfileBackgroundRevision(queryClient, background),
  })
}

export function useDeleteCoachTeachingRole() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => coachProfileService.deleteTeachingRole(id),
    onSuccess: (background) => cacheProfileBackgroundRevision(queryClient, background),
  })
}

export function useCreateCoachNotableClient() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: CoachNotableClient) => coachProfileService.createNotableClient(payload),
    onSuccess: (background) => cacheProfileBackgroundRevision(queryClient, background),
  })
}

export function useUpdateCoachNotableClient() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: CoachNotableClient }) =>
      coachProfileService.updateNotableClient(id, payload),
    onSuccess: (background) => cacheProfileBackgroundRevision(queryClient, background),
  })
}

export function useDeleteCoachNotableClient() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => coachProfileService.deleteNotableClient(id),
    onSuccess: (background) => cacheProfileBackgroundRevision(queryClient, background),
  })
}

export function useCreateCoachThoughtLeadership() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: CoachThoughtLeadership) =>
      coachProfileService.createThoughtLeadership(payload),
    onSuccess: (background) => cacheProfileBackgroundRevision(queryClient, background),
  })
}

export function useUpdateCoachThoughtLeadership() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: CoachThoughtLeadership }) =>
      coachProfileService.updateThoughtLeadership(id, payload),
    onSuccess: (background) => cacheProfileBackgroundRevision(queryClient, background),
  })
}

export function useDeleteCoachThoughtLeadership() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => coachProfileService.deleteThoughtLeadership(id),
    onSuccess: (background) => cacheProfileBackgroundRevision(queryClient, background),
  })
}

export function useCoachSessionTypes() {
  return useQuery({
    queryKey: coachProfileKeys.sessionTypes,
    queryFn: coachProfileService.getSessionTypes,
  })
}

export function useCoachVetting() {
  return useQuery({
    queryKey: coachProfileKeys.vetting,
    queryFn: coachProfileService.getVetting,
  })
}

export function useSubmitCoachVetting() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: coachProfileService.submitVetting,
    onSuccess: (status) => queryClient.setQueryData(coachProfileKeys.vetting, status),
  })
}

export function useCreateCoachSessionType() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: CoachSessionTypeWrite) => coachProfileService.createSessionType(payload),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: coachProfileKeys.sessionTypes,
      }),
  })
}

export function useUpdateCoachSessionType() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: number
      payload: CoachSessionTypeWrite
    }) => coachProfileService.updateSessionType(id, payload),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: coachProfileKeys.sessionTypes,
      }),
  })
}

export function useDeleteCoachSessionType() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: coachProfileService.deleteSessionType,
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: coachProfileKeys.sessionTypes,
      }),
  })
}

export function useUpdateCoachExpertise() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: CoachExpertiseWrite) => coachProfileService.updateExpertise(payload),
    onSuccess: (profile) => {
      queryClient.setQueryData(coachProfileKeys.all, profile)
      syncVettingAfterProfileSave(queryClient, profile)
      queryClient.invalidateQueries({ queryKey: coachProfileKeys.vetting })
    },
  })
}

export function useUpdateCoachPublicProfile() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: CoachPublicProfileWrite) =>
      coachProfileService.updatePublicProfile(payload),
    onSuccess: (profile) => {
      queryClient.setQueryData(coachProfileKeys.all, profile)
      syncVettingAfterProfileSave(queryClient, profile)
      queryClient.invalidateQueries({ queryKey: coachProfileKeys.vetting })
    },
  })
}
export function useUploadCoachPhoto() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: coachProfileService.uploadPhoto,
    onSuccess: ({ photo_url }) =>
      queryClient.setQueryData(coachProfileKeys.all, (current: unknown) =>
        current && typeof current === 'object' ? { ...current, photo_url } : current,
      ),
  })
}

export function useCoachAvailability() {
  return useQuery({
    queryKey: coachProfileKeys.availability,
    queryFn: coachProfileService.getAvailability,
    retry: false,
  })
}

export function useUpdateCoachAvailability() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: Pick<CoachWeeklyAvailability, 'timezone' | 'windows'>) =>
      coachProfileService.updateAvailability(payload),
    onMutate: async (payload) => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: coachProfileKeys.availability }),
        queryClient.cancelQueries({ queryKey: coachProfileKeys.all }),
      ])
      const previousAvailability = queryClient.getQueryData<CoachWeeklyAvailability>(
        coachProfileKeys.availability,
      )
      const previousProfile = queryClient.getQueryData(coachProfileKeys.all)
      queryClient.setQueryData(coachProfileKeys.availability, payload)
      queryClient.setQueryData(coachProfileKeys.all, (current: unknown) => {
        if (!current || typeof current !== 'object') return current
        return { ...current, timezone: payload.timezone }
      })
      return { previousAvailability, previousProfile }
    },
    onError: (_error, _payload, context) => {
      queryClient.setQueryData(coachProfileKeys.availability, context?.previousAvailability)
      queryClient.setQueryData(coachProfileKeys.all, context?.previousProfile)
    },
    onSuccess: (slots) => {
      queryClient.setQueryData(coachProfileKeys.availability, slots)
      queryClient.invalidateQueries({ queryKey: ['coach-slots'] })
      queryClient.setQueryData(coachProfileKeys.all, (current: unknown) => {
        if (!current || typeof current !== 'object') return current
        return { ...current, timezone: slots.timezone }
      })
    },
  })
}

export function useConfirmCoachAvailability() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: coachProfileService.confirmAvailability,
    onSuccess: (availability) => {
      queryClient.setQueryData(coachProfileKeys.availability, availability)
    },
  })
}

export function useCoachAvailabilityOverrides(startDate: string, endDate: string) {
  return useQuery({
    queryKey: coachProfileKeys.availabilityOverrides(startDate, endDate),
    queryFn: () => coachProfileService.getAvailabilityOverrides(startDate, endDate),
  })
}

export function useCreateCoachAvailabilityOverride() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: CoachAvailabilityOverrideWrite) =>
      coachProfileService.createAvailabilityOverride(payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['coach-availability-overrides'] }),
  })
}

export function useDeleteCoachAvailabilityOverride() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => coachProfileService.deleteAvailabilityOverride(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['coach-availability-overrides'] }),
  })
}

export function useCoachProfile() {
  return useQuery({
    queryKey: coachProfileKeys.all,
    queryFn: () => coachProfileService.getProfile(),
  })
}

export function useUpdateCoachProfile() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: CoachProfilePatch) => coachProfileService.updateProfile(payload),
    onSuccess: (profile) => {
      queryClient.setQueryData(coachProfileKeys.all, profile)
    },
  })
}

/** Persist the coach's timezone without rewriting their weekly availability. */
export function useUpdateCoachTimezone() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (timezone: string) => coachProfileService.updateProfile({ timezone }),
    onSuccess: (profile) => {
      queryClient.setQueryData(coachProfileKeys.all, profile)
      queryClient.setQueryData<CoachWeeklyAvailability>(coachProfileKeys.availability, (current) =>
        current ? { ...current, timezone: profile.timezone || current.timezone } : current,
      )
    },
  })
}

export function useUpdateMyCoachTracks() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (tracks: CoachTrack[]) => coachProfileService.updateTracks(tracks),
    onSuccess: ({ coaching_tracks }) => {
      queryClient.setQueryData(coachProfileKeys.all, (current: unknown) =>
        current && typeof current === 'object' ? { ...current, coaching_tracks } : current,
      )
      queryClient.invalidateQueries({ queryKey: ['coach-directory'] })
      queryClient.invalidateQueries({ queryKey: ['coach-vetting-admin'] })
    },
  })
}
